import { LineChart } from "@fluentui/react-charting";
import { SkeletonItem } from "@fluentui/react-components";
import { useEffect, useRef, useState } from "react";
import { ChartExportToCSV } from "../exporter/ChartExportToCSV";

interface IChartCardProps {
  chartTitle: string;
  lineChartData: any[];
  yMinValue?: number;
  yMaxValue?: number;
  legendsOverflowText: string;
  exportColumns: string[];
  yAxisTickFormat?: (t: number) => string;
  isLoading?: boolean;
}

export const ChartCard = ({
  chartTitle,
  lineChartData,
  yMinValue = 0,
  yMaxValue,
  legendsOverflowText,
  exportColumns,
  yAxisTickFormat,
  isLoading = false,
}: IChartCardProps): JSX.Element => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [chartWidth, setChartWidth] = useState<number>(500);
  const chartData = {
    chartTitle,
    lineChartData,
  };

  useEffect(() => {
    const node = containerRef.current;
    if (!node) {
      return;
    }

    const updateWidth = () => {
      setChartWidth(
        Math.max(320, Math.floor(node.getBoundingClientRect().width)),
      );
    };

    updateWidth();

    const observer = new ResizeObserver(() => {
      updateWidth();
    });

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "400px",
      }}
    >
      {isLoading ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            padding: "20px",
          }}
        >
          <SkeletonItem size={24} style={{ width: "60%" }} />
          <SkeletonItem style={{ width: "100%", height: "300px" }} />
        </div>
      ) : lineChartData.length > 0 ? (
        <>
          <ChartExportToCSV lineChartData={chartData} columns={exportColumns} />
          <LineChart
            data={chartData}
            legendsOverflowText={legendsOverflowText}
            yMinValue={yMinValue}
            yMaxValue={yMaxValue}
            height={400}
            width={chartWidth}
            margins={{ left: 35, top: 20, bottom: 50, right: 0 }}
            yAxisTickFormat={yAxisTickFormat}
          />
        </>
      ) : null}
    </div>
  );
};
